import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Select from "react-select";
import { getCode, getNames } from "country-list";
import {
  useOnboardFromParsed,
  useParseCaptableExcel,
  useParseCaptableAI,
} from "../../queries/newCapTable/companyOnboarding";
import {
  isTerminalCaptableBatch,
  useBackgroundJobsStore,
} from "../../store/useBackgroundJobsStore";
import { useAccountUsers } from "../../queries/companyUsers";
import { queryClient } from "../../queries";
import { getUserCompanies } from "../../api/auth";
import {
  ParsedCaptableExcel,
  ParsedCompanyDetails,
  ParsedShareholding,
} from "../../types/ExcelParsingOnboarding";
import { Box, HStack, VStack } from "../../components/utils";
import {
  CheckBoxWithLabel,
  Input,
  Label,
  Required,
} from "../../components/shared/InputField";
import { currencies } from "../../constants/CompanyCurrencyType";
import {
  ButtonSize,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import ValidationErrorsDialog from "../../modals/ValidationErrorsDialog";
import Tooltip from "../../components/shared/Tooltip";
import ShareholdingDetailsGrid from "./ShareholdingDetailsGrid";
import { downloadS3File } from "../../utils/DownloadFile";
import { formatWithCommas } from "./AddCompanyModal";

type UploadStatus = "pending" | "uploading" | "success" | "error";

type UploadFile = {
  id: string;
  file: File;
  status: UploadStatus;
  message?: string;
  sheetNames?: string[];
  selectedSheet?: string;
  isReadingSheets?: boolean;
  batchId?: string;
  backendFileId?: string;
  originalSize?: number;
};

type ParsingMode = "ai" | "template";

const formatBatchStatus = (status?: string) =>
  status
    ? status
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Pending";

const ALLOWED_EXTENSIONS = [".xlsm", ".xlsx"];

const countries = getNames().sort();

// ISO country code -> one of the currency codes supported in CompanyCurrencyType.ts.
const COUNTRY_CODE_TO_CURRENCY: Record<string, string> = {
  IN: "INR",
  US: "USD",
  GB: "GBP",
  AE: "AED",
  AU: "AUD",
  CN: "CNY",
  JP: "JPY",
  SG: "SGD",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  PT: "EUR",
  IE: "EUR",
  FI: "EUR",
  GR: "EUR",
  LU: "EUR",
  SK: "EUR",
  SI: "EUR",
  EE: "EUR",
  LV: "EUR",
  LT: "EUR",
  CY: "EUR",
  MT: "EUR",
  HR: "EUR",
};

const CURRENCY_TO_JURISDICTION: Record<string, string> = {
  INR: "India",
  USD: "United States",
  GBP: "United Kingdom",
  AED: "United Arab Emirates",
  AUD: "Australia",
  CNY: "China",
  JPY: "Japan",
  SGD: "Singapore",
  EUR: "Germany",
};

// Resolves a country name (as shown in the Jurisdiction dropdown) to the matching
// "CODE - symbol" entry in the Company Currency dropdown, e.g. "India" -> "INR - ₹".
export const getCurrencyForCountry = (
  countryName: string
): string | undefined => {
  const countryCode = getCode(countryName);
  const currencyCode = countryCode
    ? COUNTRY_CODE_TO_CURRENCY[countryCode]
    : undefined;
  if (!currencyCode) return undefined;
  return currencies.find((entry) => entry.startsWith(`${currencyCode} -`));
};

export const getJurisdictionForCurrency = (
  currency?: string | null
): string | undefined => {
  if (!currency) return undefined;
  const code = currency.trim().split(/\s|-/)[0]?.toUpperCase();
  return CURRENCY_TO_JURISDICTION[code];
};

// Merges parsed company details onto the known/initial ones, only letting a
// parsed value win when it's actually present — otherwise a field the sheet
// didn't contain (returned as null/undefined/"") would blank out a value we
// already had (e.g. valuation carried over from the company record).
const mergeCompanyDetails = (
  initial: Partial<ParsedCompanyDetails> | undefined,
  parsed: Partial<ParsedCompanyDetails> | undefined
): Partial<ParsedCompanyDetails> => {
  const merged: Partial<ParsedCompanyDetails> = { ...initial };
  Object.entries(parsed || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      (merged as Record<string, unknown>)[key] = value;
    }
  });
  return merged;
};

const DEFAULT_JURISDICTION = "India";

// Fills Jurisdiction/Company Currency with sensible defaults when the parse
// didn't find them, so the form doesn't start on two required blank fields.
const applyDefaultCompanyDetails = (
  details: Partial<ParsedCompanyDetails>
): Partial<ParsedCompanyDetails> => {
  const next: Partial<ParsedCompanyDetails> = { ...details };
  if (!next.jurisdiction) {
    next.jurisdiction =
      getJurisdictionForCurrency(next.companyCurrency) || DEFAULT_JURISDICTION;
  }
  if (!next.companyCurrency) {
    const defaultCurrency = getCurrencyForCountry(next.jurisdiction);
    if (defaultCurrency) next.companyCurrency = defaultCurrency;
  }
  return next;
};

const PARSING_OPTIONS: {
  mode: ParsingMode;
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  disabled?: boolean;
}[] = [
  {
    mode: "template",
    icon: "mdi:file-table-outline",
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    title: "Template Upload",
    description:
      "Upload our structured Excel template for fast, precise, rule-based onboarding.",
  },
  {
    mode: "ai",
    icon: "mdi:auto-fix",
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    title: "Smart Upload (AI Parsing)",
    description:
      "Upload a file in any layout — our AI reads it and extracts company and cap table details automatically.",
  },
];

const COMPANY_DETAIL_FIELDS: {
  key: keyof ParsedCompanyDetails;
  label: string;
  type?: "text" | "number" | "date";
  required?: boolean;
}[] = [
  { key: "name", label: "Company Name", required: true },
  { key: "cin", label: "CIN", required: true },
  { key: "companyCurrency", label: "Company Currency", required: true },
  { key: "dba", label: "DBA" },
  { key: "jurisdiction", label: "Jurisdiction", required: true },
  { key: "valuation", label: "Valuation", type: "number", required: true },
  { key: "sharePrice", label: "Share Price", type: "number", required: true },
  { key: "lastRoundName", label: "Last Round Name" },
  { key: "lastRoundSize", label: "Last Round Size", type: "number" },
  { key: "lastRoundDate", label: "Last Round Date", type: "date" },
  { key: "captableDate", label: "Captable Date", type: "date" },
  {
    key: "totalNumberOfShares",
    label: "Total Number of Shares",
    type: "number",
  },
  // { key: "total", label: "Total", type: "number" },
  // { key: "verification", label: "Verification", type: "number" },
  // { key: "mgt14FdbOrPaidUp", label: "MGT-14 FDB or Paid Up" },
  // {
  //   key: "mgt14ProvidesIndividualShareholderList",
  //   label: "MGT-14 Provides Individual Shareholder List",
  // },
  // { key: "mgt14Attachment", label: "MGT-14 Attachment" },
  // { key: "pas3FiledAfterMgt7", label: "PAS-3 Filed After MGT-7" },
  // { key: "nonPricedSecuritiesIssued", label: "Non-Priced Securities Issued" },
  // {
  //   key: "conversionOfSecuritiesToEquity",
  //   label: "Conversion of Securities to Equity",
  // },
  // { key: "split", label: "Split" },
  // { key: "otherRemarks", label: "Other Remarks" },
];

const isAllowed = (file: File) =>
  ALLOWED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const readSheetNames = async (file: File): Promise<string[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", bookSheets: true });
  return workbook.SheetNames;
};

const formatSize = (size: number) =>
  size > 1000000
    ? `${(size / 1000000).toFixed(2)} MB`
    : `${(size / 1000).toFixed(2)} KB`;

const COMMA_FORMATTED_FIELD_KEYS: (keyof ParsedCompanyDetails)[] = [
  "valuation",
  "sharePrice",
];

// Truncates a long sheet name with an ellipsis (both as the selected value
// and in the menu list) while exposing the full name via a native tooltip
// on hover — a plain `title` on the select's wrapper doesn't work for the
// menu since it's portaled out to document.body.
const formatSheetOptionLabel = (option: { label: string }) => (
  <span className="block truncate" title={option.label}>
    {option.label}
  </span>
);

// Matches the sizing/border/margin of the shared <Input> component (InputField.tsx)
// so react-select dropdowns sit flush with plain text fields in the same grid row.
export const inputMatchingSelectStyles = {
  container: (base: any) => ({ ...base, marginTop: "0.375rem" }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: " 2.8rem",
    borderRadius: "0.375rem",
    borderColor: state.isFocused ? "#C6CCDC" : "#DBDFE9",
    boxShadow: "none",
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "2px 8px",
  }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, padding: "0 4px" }),
  indicatorSeparator: () => ({ display: "none" }),
};

type Props = {
  // Prefills the company-details form once a file is parsed, so attaching a
  // cap table to an already-created draft company doesn't require retyping
  // details captured by "Add Company". Parsed values still win over these.
  initialCompanyDetails?: Partial<ParsedCompanyDetails>;
  // uuid of an already-created company this cap table should attach to, so
  // editing name/CIN away from what was used to create the company doesn't
  // spin up a duplicate company on submit.
  companyId?: string;
  defaultExpanded?: boolean;
  onOnboarded?: () => void;
  // When embedded alongside other content (e.g. the onboarding table), the
  // card caps its own height and scrolls internally so it doesn't push the
  // rest of the page down. On a page where this card *is* the content,
  // that inner scrollbar just fights with the page's own — set this to let
  // the card grow to fit and leave scrolling to the page.
  fullHeight?: boolean;
};

export default function OnboardCompanyUploadCard({
  initialCompanyDetails,
  companyId,
  defaultExpanded = false,
  onOnboarded,
  fullHeight = false,
}: Props = {}) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [parsingMode, setParsingMode] = useState<ParsingMode | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dialog, setDialog] = useState<{ open: boolean; data?: string[] }>({
    open: false,
  });
  const [parsedResult, setParsedResult] = useState<ParsedCaptableExcel | null>(
    null
  );
  const [completedResults, setCompletedResults] = useState<
    { filename: string; data: ParsedCaptableExcel }[]
  >([]);
  const [selectedResultFilename, setSelectedResultFilename] = useState<
    string | null
  >(null);
  const [companyDetailsForm, setCompanyDetailsForm] = useState<
    Partial<ParsedCompanyDetails>
  >({});
  const [shareholdingRows, setShareholdingRows] = useState<
    ParsedShareholding[]
  >([]);
  const [activeDetailsTab, setActiveDetailsTab] = useState<
    "company" | "shareholding"
  >("company");
  const [lastParsedFile, setLastParsedFile] = useState<UploadFile | null>(null);
  const [reparseSheet, setReparseSheet] = useState<string | null>(null);
  const [isReparsing, setIsReparsing] = useState(false);
  const [giveAccessToAllUsers, setGiveAccessToAllUsers] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardedCompanyId, setOnboardedCompanyId] = useState<string | null>(
    companyId ?? null
  );

  useEffect(() => {
    setOnboardedCompanyId(companyId ?? null);
  }, [companyId]);

  const fileRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: parseCaptableExcel } = useParseCaptableExcel();
  const { mutateAsync: parseCaptableAI } = useParseCaptableAI();
  const { mutateAsync: onboardFromParsed } = useOnboardFromParsed();
  const { data: accountUsers } = useAccountUsers();
  const registerBatch = useBackgroundJobsStore((state) => state.registerBatch);
  const retryBatchFile = useBackgroundJobsStore((state) => state.retryFile);
  const forgetBatch = useBackgroundJobsStore((state) => state.forgetBatch);
  const batches = useBackgroundJobsStore((state) => state.batches);
  const focusedBatchId = useBackgroundJobsStore(
    (state) => state.focusedBatchId
  );
  const clearedBatchId = useBackgroundJobsStore(
    (state) => state.clearedBatchId
  );
  const clearClearedBatch = useBackgroundJobsStore(
    (state) => state.clearClearedBatch
  );
  const batchContexts = useBackgroundJobsStore((state) => state.batchContexts);
  const routeBatchId = (location.state as { batchId?: string } | null)?.batchId;
  const belongsToCurrentCompany = (batchId?: string) => {
    if (!batchId || !batches[batchId]) return false;
    const context = batchContexts[batchId];
    if (companyId) {
      return (context?.companyId || batches[batchId].companyId) === companyId;
    }
    return context?.returnTo === location.pathname;
  };
  const isRestorableBatch = (batchId?: string) =>
    belongsToCurrentCompany(batchId) &&
    Boolean(batchId && !isTerminalCaptableBatch(batches[batchId]));
  const contextualBatchId = Object.entries(batchContexts)
    .filter(([batchId]) => isRestorableBatch(batchId))
    .sort(
      ([firstId], [secondId]) =>
        new Date(
          batches[secondId]?.updatedAt || batches[secondId]?.createdAt || 0
        ).getTime() -
        new Date(
          batches[firstId]?.updatedAt || batches[firstId]?.createdAt || 0
        ).getTime()
    )[0]?.[0];
  const restoredBatchId = isRestorableBatch(routeBatchId)
    ? routeBatchId
    : isRestorableBatch(focusedBatchId)
    ? focusedBatchId
    : contextualBatchId;
  const hasProcessingBatch = files.some((item) =>
    Boolean(
      item.batchId &&
        batches[item.batchId] &&
        !isTerminalCaptableBatch(batches[item.batchId])
    )
  );

  const selectCompletedResult = (
    data: ParsedCaptableExcel,
    filename?: string
  ) => {
    if (filename) setSelectedResultFilename(filename);
    setParsedResult(data);
    setCompanyDetailsForm(
      applyDefaultCompanyDetails(
        mergeCompanyDetails(initialCompanyDetails, data.companyDetails)
      )
    );
    setShareholdingRows(data.shareholding || []);
    setActiveDetailsTab("company");
  };

  const localBatchKey = files
    .map((item) => item.batchId)
    .filter((value): value is string => Boolean(value))
    .sort()
    .join(",");

  // The screen is a projection of the global API-backed cache. It can unmount
  // during processing and rebuild completed (including partial) results later.
  useEffect(() => {
    if (clearedBatchId) {
      setFiles((current) =>
        current.filter((item) => item.batchId !== clearedBatchId)
      );
      setParsedResult(null);
      setCompletedResults([]);
      setSelectedResultFilename(null);
      setCompanyDetailsForm({});
      setShareholdingRows([]);
      setLastParsedFile(null);
      setActiveDetailsTab("company");
      clearClearedBatch();
      return;
    }
    if (restoredBatchId && batches[restoredBatchId]) {
      setExpanded(true);
      setParsingMode("ai");
    }
    setFiles((current) => {
      let source = current;
      const focusedBatch = restoredBatchId
        ? batches[restoredBatchId]
        : undefined;
      if (
        focusedBatch &&
        !current.some((item) => item.batchId === restoredBatchId)
      ) {
        source = (focusedBatch.files || []).map((remote, index) => ({
          id: `${restoredBatchId}-${remote.fileId || remote.id || index}`,
          file: new File(
            [],
            remote.filename ||
              remote.fileName ||
              remote.originalName ||
              remote.originalFilename ||
              remote.name ||
              `File ${index + 1}`
          ),
          batchId: restoredBatchId,
          backendFileId: remote.fileId || remote.id,
          originalSize: remote.size || remote.fileSize || remote.sizeBytes,
          sheetNames: ["Default"],
          status: (remote.status === "COMPLETED" && !remote.data
            ? "error"
            : remote.status === "COMPLETED"
            ? "success"
            : remote.status === "FAILED"
            ? "error"
            : "uploading") as UploadStatus,
          message:
            remote.status === "COMPLETED" && !remote.data
              ? "Completed, but parsed result data was unavailable"
              : remote.errorMessage ||
                remote.error ||
                remote.stage ||
                remote.status,
        }));
      }
      return source.map((item) => {
        if (!item.batchId) return item;
        const batch = batches[item.batchId];
        if (!batch) return item;
        const index = current
          .filter((candidate) => candidate.batchId === item.batchId)
          .indexOf(item);
        const remote =
          batch.files?.find(
            (file) =>
              (file.filename ||
                file.fileName ||
                file.originalName ||
                file.originalFilename ||
                file.name) === item.file.name
          ) || batch.files?.[index];
        if (!remote) return item;
        return {
          ...item,
          backendFileId: remote.fileId || remote.id || item.backendFileId,
          status:
            remote.status === "COMPLETED" && !remote.data
              ? "error"
              : remote.status === "COMPLETED"
              ? "success"
              : remote.status === "FAILED"
              ? "error"
              : "uploading",
          message:
            remote.status === "COMPLETED" && !remote.data
              ? "Completed, but parsed result data was unavailable"
              : remote.errorMessage ||
                remote.error ||
                remote.stage ||
                remote.status,
        };
      });
    });

    const relevantBatchIds = restoredBatchId
      ? [restoredBatchId]
      : localBatchKey.split(",").filter(Boolean);
    const relevantBatches = relevantBatchIds
      .map((batchId) => batches[batchId])
      .filter(Boolean);
    const completed = relevantBatches.flatMap((batch) =>
      (batch.files || [])
        .filter((file) => file.status === "COMPLETED" && file.data)
        .map((file, index) => ({
          filename:
            file.filename ||
            file.fileName ||
            file.originalName ||
            file.originalFilename ||
            file.name ||
            `File ${index + 1}`,
          data: file.data as ParsedCaptableExcel,
        }))
    );
    if (completed.length > 0) {
      setCompletedResults(completed);
      const selected =
        completed.find(
          (result) => result.filename === selectedResultFilename
        ) || completed[0];
      selectCompletedResult(selected.data, selected.filename);
    }
  }, [
    batches,
    restoredBatchId,
    localBatchKey,
    clearedBatchId,
    clearClearedBatch,
  ]);

  const selectParsingMode = (mode: ParsingMode) => {
    if (mode === parsingMode) return;
    setParsingMode(mode);
    setFiles([]);
    setParsedResult(null);
    setCompletedResults([]);
    setSelectedResultFilename(null);
    setCompanyDetailsForm({});
    setShareholdingRows([]);
    setLastParsedFile(null);
    setReparseSheet(null);
    setActiveDetailsTab("company");
    setOnboardedCompanyId(companyId ?? null);
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const invalid = incoming.filter((f) => !isAllowed(f));
    if (invalid.length > 0) {
      toast(
        `Invalid file type, allowed file types are ${ALLOWED_EXTENSIONS.join(
          ", "
        )}`,
        { type: "error", autoClose: 3000 }
      );
    }
    const valid = incoming.filter(isAllowed);
    const added = valid.map((file, index) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${index}`,
      file,
      status: "pending" as UploadStatus,
      isReadingSheets: true,
    }));
    setFiles((prev) => [...prev, ...added]);

    added.forEach((item) => {
      readSheetNames(item.file)
        .then((sheetNames) => {
          updateFile(item.id, { sheetNames, isReadingSheets: false });
        })
        .catch(() => {
          updateFile(item.id, { isReadingSheets: false });
        });
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const updateFile = (id: string, patch: Partial<UploadFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const retryFile = async (item: UploadFile) => {
    if (item.batchId && item.backendFileId) {
      await retryBatchFile(item.batchId, item.backendFileId);
      return;
    }
    updateFile(item.id, { status: "pending", message: "Ready to retry" });
  };

  const uploadAIBatch = async (items: UploadFile[]) => {
    const formData = new FormData();
    items.forEach((item) => formData.append("files", item.file));
    formData.append(
      "fileOptions",
      JSON.stringify(items.map((item) => ({ sheetName: item.selectedSheet })))
    );
    items.forEach((item) =>
      updateFile(item.id, { status: "uploading", message: "Queued" })
    );
    const { batchId } = await parseCaptableAI({
      formData,
      companyId: onboardedCompanyId ?? undefined,
    });
    items.forEach((item) =>
      updateFile(item.id, { batchId, status: "uploading", message: "QUEUED" })
    );
    await registerBatch(
      batchId,
      {
        batchId,
        companyId: onboardedCompanyId,
        status: "QUEUED",
        files: items.map((item) => ({
          filename: item.file.name,
          status: "QUEUED",
        })),
      },
      {
        companyId: onboardedCompanyId || undefined,
        companyName: initialCompanyDetails?.name || undefined,
        returnTo: location.pathname,
        filenames: items.map((item) => item.file.name),
        files: items.map((item) => ({
          filename: item.file.name,
          size: item.file.size,
        })),
        sourceFiles: items.map((item) => item.file),
        draftCompanyDetails: initialCompanyDetails,
      }
    );
  };

  const parseAgainFile = async (item: UploadFile) => {
    if (parsingMode !== "ai" || isUploading) return;
    if (item.file.size === 0) return;
    setIsUploading(true);
    try {
      await uploadAIBatch([item]);
    } catch (error: any) {
      updateFile(item.id, {
        status: "error",
        message: error?.message || "Unable to queue parsing",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const uploadAll = async () => {
    const pending = files.filter((f) => f.status === "pending");
    if (pending.length === 0) return;
    setIsUploading(true);
    if (parsingMode === "ai") {
      try {
        await uploadAIBatch(pending);
      } catch (error: any) {
        pending.forEach((item) =>
          updateFile(item.id, { status: "error", message: error?.message })
        );
      } finally {
        setIsUploading(false);
      }
      return;
    }
    let successCount = 0;
    for (const item of pending) {
      updateFile(item.id, { status: "uploading", message: undefined });
      const formData = new FormData();
      formData.append("file", item.file);
      if (item.selectedSheet) {
        formData.append("sheetName", item.selectedSheet);
      }
      try {
        const result = // eslint-disable-next-line no-await-in-loop
          (await parseCaptableExcel({
            formData,
            companyId: onboardedCompanyId ?? undefined,
          })) as ParsedCaptableExcel;
        updateFile(item.id, { status: "success", message: "Parsed" });
        successCount += 1;
        const hasCompanyDetails =
          result?.companyDetails &&
          Object.keys(result.companyDetails).length > 0;
        const hasShareholding =
          result?.shareholding && result.shareholding.length > 0;
        if (hasCompanyDetails || hasShareholding) {
          setParsedResult(result);
          setCompletedResults([{ filename: item.file.name, data: result }]);
          setSelectedResultFilename(item.file.name);
          setCompanyDetailsForm(
            applyDefaultCompanyDetails(
              mergeCompanyDetails(initialCompanyDetails, result.companyDetails)
            )
          );
          setShareholdingRows(result.shareholding || []);
          setLastParsedFile(item);
          setReparseSheet(null);
          setActiveDetailsTab("company");
        } else {
          toast(
            `${item.file.name} was parsed, but no company details or shareholding rows were found. Check that the sheet selected matches the expected template.`,
            { type: "warning", autoClose: 4000 }
          );
        }
      } catch (error: any) {
        const errors = error?.response?.data?.errors;
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Parsing failed";
        if (errors && errors.length > 0) {
          setDialog({ open: true, data: errors });
        }
        updateFile(item.id, { status: "error", message: errorMessage });
        toast(`Failed to parse ${item.file.name}: ${errorMessage}`, {
          type: "error",
          autoClose: 4000,
        });
      }
    }
    setIsUploading(false);
    if (successCount > 0) {
      toast(
        `${successCount} file${
          successCount === 1 ? "" : "s"
        } parsed successfully!`,
        { type: "success", autoClose: 2000 }
      );
    }
  };

  const reparseWithSheet = async () => {
    if (!lastParsedFile) return;
    const sheetLabel = reparseSheet
      ? `sheet "${reparseSheet}"`
      : "default sheets";
    setIsReparsing(true);
    const formData = new FormData();
    formData.append(
      parsingMode === "ai" ? "files" : "file",
      lastParsedFile.file
    );
    if (parsingMode === "ai") {
      formData.append(
        "fileOptions",
        JSON.stringify([{ sheetName: reparseSheet }])
      );
    }
    if (reparseSheet) {
      formData.append("sheetName", reparseSheet);
    }
    try {
      if (parsingMode === "ai") {
        await uploadAIBatch([lastParsedFile]);
        toast(`Queued re-parse using ${sheetLabel}`, {
          type: "success",
          autoClose: 2000,
        });
        return;
      }
      const parse = parseCaptableExcel;
      const result = (await parse({
        formData,
        companyId: onboardedCompanyId ?? undefined,
      })) as ParsedCaptableExcel;
      const hasCompanyDetails =
        result?.companyDetails && Object.keys(result.companyDetails).length > 0;
      const hasShareholding =
        result?.shareholding && result.shareholding.length > 0;
      if (hasCompanyDetails || hasShareholding) {
        setParsedResult(result);
        setCompletedResults([
          { filename: lastParsedFile.file.name, data: result },
        ]);
        setSelectedResultFilename(lastParsedFile.file.name);
        setCompanyDetailsForm(
          applyDefaultCompanyDetails(
            mergeCompanyDetails(initialCompanyDetails, result.companyDetails)
          )
        );
        setShareholdingRows(result.shareholding || []);
        toast(`Re-parsed using ${sheetLabel}`, {
          type: "success",
          autoClose: 2000,
        });
      } else {
        toast(
          `No company details or shareholding rows found using ${sheetLabel}.`,
          { type: "warning", autoClose: 4000 }
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Parsing failed";
      toast(`Failed to parse using ${sheetLabel}: ${errorMessage}`, {
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setIsReparsing(false);
    }
  };

  const isFieldMissing = (
    field: typeof COMPANY_DETAIL_FIELDS[number]
  ): boolean => {
    const value = companyDetailsForm[field.key];
    if (value === undefined || value === null || value === "") return true;
    // Valuation/Share Price must be an actual positive number — a stray 0
    // (or non-numeric leftover like ".") shouldn't count as "provided".
    if (field.type === "number") {
      const numericValue = Number(value);
      return Number.isNaN(numericValue) || numericValue <= 0;
    }
    return false;
  };

  const missingRequiredFields = COMPANY_DETAIL_FIELDS.filter(
    (field) => field.required && isFieldMissing(field)
  );
  const missingShareholdingFields = shareholdingRows.some(
    (row) =>
      !row.shareholderName ||
      !row.numberOfShares ||
      Number(row.numberOfShares) <= 0
  );
  // The AI list describes what was absent in the raw workbook. Once the user
  // edits a value, the form is authoritative; do not keep showing a blocking
  // error for a field that is now populated.
  const missingParsedFields =
    parsingMode === "ai"
      ? []
      : parsedResult?.missingCompanyFields?.filter(Boolean) || [];
  const isOnboardDisabled =
    missingRequiredFields.length > 0 || missingShareholdingFields;

  const allAccountUserEmails = (accountUsers || [])
    .map((u: any) => u?.emailId || u?.email)
    .filter((email): email is string => Boolean(email));

  const buildOnboardCompanyDetails = (): ParsedCompanyDetails => {
    const normalized: Record<string, unknown> = { ...companyDetailsForm };
    COMPANY_DETAIL_FIELDS.forEach((field) => {
      const value = normalized[field.key];
      if (field.type === "number" && value !== undefined && value !== "") {
        normalized[field.key] = Number(value);
      }
    });
    return normalized as ParsedCompanyDetails;
  };

  const toNumberOrKeep = (value: unknown) =>
    value === undefined || value === null || value === ""
      ? value
      : Number(value);

  const buildOnboardShareholding = (): ParsedShareholding[] =>
    shareholdingRows.map((row) => ({
      ...row,
      numberOfShares: toNumberOrKeep(row.numberOfShares) as number,
      shareholderFDBPercentage: toNumberOrKeep(
        row.shareholderFDBPercentage
      ) as number,
      pricePerShare: toNumberOrKeep(row.pricePerShare) as number | undefined,
    }));

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

  // The onboard-from-parsed endpoint is processed by the backend task
  // manager, so the company's draft/documents flags may not be updated yet
  // by the time this request resolves. Poll until the list reflects the
  // attached captable (or give up after a few tries) instead of trusting a
  // single immediate refetch, otherwise the UI can keep showing stale data
  // until the user manually refreshes.
  const pollUntilCaptableAttached = async (
    companyId: string,
    { attempts = 8, delayMs = 1500 } = {}
  ): Promise<void> => {
    if (attempts <= 0) return;
    const companies = await getUserCompanies();
    queryClient.setQueryData("userCompanies", companies);
    const company = companies.find((c) => c.uuid === companyId);
    const hasDocuments = (company?.companyDocuments?.length ?? 0) > 0;
    if (company && !company.isDraftCompany && hasDocuments) {
      // userContext (which drives authStore.user.isDraftCompany and, in turn,
      // the MainSideNavbar visibility gate in MainLayout) is disabled after
      // login, so it won't pick up this change on its own — force it, same
      // fix as RemoveCaptableModal.tsx.
      queryClient.refetchQueries("userContext");
      return;
    }
    await sleep(delayMs);
    await pollUntilCaptableAttached(companyId, {
      attempts: attempts - 1,
      delayMs,
    });
  };

  const handleOnboard = async () => {
    setIsOnboarding(true);
    try {
      const result = await onboardFromParsed({
        payload: {
          companyDetails: buildOnboardCompanyDetails(),
          shareholding: buildOnboardShareholding(),
          ...(giveAccessToAllUsers ? { emails: allAccountUserEmails } : {}),
          ...(onboardedCompanyId ? { companyId: onboardedCompanyId } : {}),
        },
        file: lastParsedFile?.file,
      });
      setOnboardedCompanyId(result.companyId);
      toast(
        `Company onboarded successfully! (Company ID: ${result.companyId})`,
        { type: "success", autoClose: 3000 }
      );
      setFiles([]);
      setParsedResult(null);
      setCompanyDetailsForm({});
      setShareholdingRows([]);
      setLastParsedFile(null);
      setReparseSheet(null);
      setGiveAccessToAllUsers(false);
      setActiveDetailsTab("company");
      await pollUntilCaptableAttached(result.companyId);
      onOnboarded?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Onboarding failed";
      toast(`Failed to onboard company: ${errorMessage}`, {
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setIsOnboarding(false);
    }
  };

  const pendingCount = files.filter((f) => f.status === "pending").length;

  return (
    <Box className="bg-white rounded-md shadow border border-stone-200 w-full">
      <HStack className="items-center justify-between px-6 py-4 select-none">
        <HStack className="items-center gap-3">
          <Icon
            icon="material-symbols:cloud-upload-outline"
            className="text-primary"
            height={22}
          />
          <VStack>
            <p className="text-base font-medium text-headerColor">
              Onboard Companies
            </p>
            <p className="text-xs1 text-descriptionColor">
              Upload one or more excel files to onboard companies
            </p>
          </VStack>
        </HStack>
        <Icon
          icon="mdi:chevron-down"
          height={24}
          onClick={() => setExpanded((prev) => !prev)}
          className={`text-stone-500 cursor-pointer hover:text-stone-700 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </HStack>

      {expanded && (
        <VStack
          className={`border-t border-stone-100 min-h-0 ${
            fullHeight ? "" : "max-h-[95vh]"
          }`}
        >
          <VStack
            className={`px-6 pb-6 gap-4 pt-4 min-h-0 ${
              fullHeight ? "" : "overflow-y-auto"
            }`}
          >
            <VStack className="gap-1">
              <p className="text-sm font-semibold text-stone-800">
                Select Onboarding Method
              </p>
            </VStack>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PARSING_OPTIONS.map((option) => {
                const isSelected = parsingMode === option.mode;
                return (
                  <button
                    key={option.mode}
                    type="button"
                    disabled={option.disabled}
                    onClick={() =>
                      !option.disabled && selectParsingMode(option.mode)
                    }
                    title={option.disabled ? "Coming soon" : undefined}
                    className={`group relative flex min-h-[104px] flex-row items-center gap-3 rounded-xl border p-5 text-left transition-all ${
                      option.disabled
                        ? "cursor-not-allowed opacity-50 border-stone-200 bg-stone-50"
                        : isSelected
                        ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
                        : "border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50"
                    }`}
                  >
                    {option.disabled && (
                      <span className="absolute right-3 top-3 rounded-full bg-stone-200 px-2 py-0.5 text-xxs font-semibold text-stone-500">
                        Coming soon
                      </span>
                    )}
                    {!option.disabled && (
                      <Icon
                        icon={
                          isSelected ? "mdi:check-circle" : "mdi:circle-outline"
                        }
                        height={18}
                        className={`absolute right-3 top-3 ${
                          isSelected
                            ? "text-primary"
                            : "text-stone-300 group-hover:text-stone-400"
                        }`}
                      />
                    )}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${option.iconBg}`}
                    >
                      <Icon
                        icon={option.icon}
                        height={22}
                        className={option.iconColor}
                      />
                    </div>
                    <VStack className="gap-0.5 pr-5">
                      <p className="text-sm font-semibold text-stone-800">
                        {option.title}
                      </p>
                      <p className="text-xs leading-snug text-stone-500">
                        {option.description}
                      </p>
                    </VStack>
                  </button>
                );
              })}
            </div>

            {parsingMode && (
              <>
                <VStack className="rounded-md border border-stone-200 overflow-hidden ">
                  <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={handleDrop}
                    className={`flex flex-row items-center justify-center gap-2 py-4 px-6 text-center cursor-pointer transition-colors ${
                      isDragging
                        ? "bg-primary/5"
                        : "hover:bg-stone-100 bg-stone-50"
                    }`}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".xlsx, .xlsm"
                      multiple
                      hidden
                      onChange={(e) => {
                        addFiles(e.target.files);
                        if (fileRef.current) fileRef.current.value = "";
                      }}
                    />
                    <Icon
                      icon="material-symbols:cloud-upload"
                      className="text-primary shrink-0"
                      height={22}
                    />
                    <p className="text-sm font-medium text-stone-700">
                      Drag & drop excel files here, or{" "}
                      <span className="text-primary underline">browse</span>
                    </p>
                    <p className="text-xs text-stone-400">
                      (Supports multiple {ALLOWED_EXTENSIONS.join(", ")} files)
                    </p>
                  </div>

                  {files.length > 0 && (
                    <VStack className="divide-y divide-stone-100 border-t border-stone-200 max-h-72 overflow-y-auto">
                      {files.map((item) => (
                        <VStack
                          key={item.id}
                          className="gap-2 px-4 py-2 shrink-0"
                        >
                          <HStack className="items-center justify-between">
                            <HStack className="items-center gap-3 min-w-0">
                              <Icon
                                icon="mdi:file-excel-outline"
                                className="text-green-700 shrink-0"
                                height={22}
                              />
                              <VStack className="min-w-0">
                                <p
                                  className="text-sm text-stone-700 truncate max-w-xs"
                                  title={item.file.name}
                                >
                                  {item.file.name}
                                </p>
                                <p className="text-xxs text-stone-400">
                                  {formatSize(
                                    item.originalSize ?? item.file.size
                                  )}
                                  {item.message ? ` • ${item.message}` : ""}
                                </p>
                              </VStack>
                            </HStack>
                            <HStack className="items-center gap-2 shrink-0">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xxs font-medium ${
                                  item.status === "success"
                                    ? "bg-green-100 text-green-700"
                                    : item.status === "error"
                                    ? "bg-red-100 text-red-700"
                                    : item.status === "uploading"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-stone-100 text-stone-600"
                                }`}
                              >
                                {item.status === "success"
                                  ? "Uploaded"
                                  : item.status === "error"
                                  ? "Failed"
                                  : item.status === "uploading"
                                  ? formatBatchStatus(
                                      item.message || "UPLOADING"
                                    )
                                  : formatBatchStatus(
                                      item.message || "PENDING"
                                    )}
                              </span>
                              {item.status === "error" && !isUploading && (
                                <button
                                  type="button"
                                  className="text-xs font-medium text-primary underline hover:text-primary/80"
                                  onClick={() => retryFile(item)}
                                >
                                  Retry
                                </button>
                              )}
                            </HStack>
                          </HStack>

                          {item.isReadingSheets && (
                            <HStack className="items-center gap-2 pl-9">
                              <Icon
                                icon="lucide:loader-2"
                                className="animate-spin text-stone-400"
                                height={14}
                              />
                              <p className="text-xxs text-stone-400">
                                Reading sheet names…
                              </p>
                            </HStack>
                          )}

                          {!item.isReadingSheets &&
                            item.sheetNames &&
                            item.sheetNames.length > 0 && (
                              <HStack className="items-center gap-2 pl-9">
                                <p className="text-xxs text-stone-500 shrink-0">
                                  Sheet name (optional)
                                </p>
                                <Box className="w-52">
                                  <Select
                                    isDisabled={item.status === "uploading"}
                                    options={item.sheetNames.map((name) => ({
                                      label: name,
                                      value: name,
                                    }))}
                                    value={
                                      item.selectedSheet
                                        ? {
                                            label: item.selectedSheet,
                                            value: item.selectedSheet,
                                          }
                                        : null
                                    }
                                    onChange={(option) =>
                                      updateFile(item.id, {
                                        selectedSheet: option?.value,
                                        // Picking a different sheet after a
                                        // finished parse (success or error) is
                                        // a retry — move it back to pending so
                                        // the next "Upload" pass picks it up.
                                        ...(item.status !== "pending" &&
                                        item.status !== "uploading"
                                          ? {
                                              status: "pending",
                                              message: undefined,
                                            }
                                          : {}),
                                      })
                                    }
                                    formatOptionLabel={formatSheetOptionLabel}
                                    isClearable
                                    placeholder="Default"
                                    classNamePrefix="select2-selection"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1400,
                                      }),
                                      control: (base) => ({
                                        ...base,
                                        minWidth: "100%",
                                      }),
                                    }}
                                  />
                                </Box>
                                {parsingMode === "ai" &&
                                  (item.status === "success" ||
                                    item.status === "error") && (
                                    <button
                                      type="button"
                                      disabled={
                                        isUploading || item.file.size === 0
                                      }
                                      className="text-sm font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() => {
                                        setLastParsedFile(item);
                                        setReparseSheet(
                                          item.selectedSheet || null
                                        );
                                        parseAgainFile(item).catch(
                                          () => undefined
                                        );
                                      }}
                                    >
                                      Parse again
                                    </button>
                                  )}
                              </HStack>
                            )}
                        </VStack>
                      ))}
                    </VStack>
                  )}
                </VStack>

                <HStack className="justify-end items-center gap-4 pt-2 border-t border-stone-100">
                  {files.length > 0 && (
                    <button
                      type="button"
                      disabled={isUploading || hasProcessingBatch}
                      onClick={() => {
                        const batchIds = new Set(
                          files
                            .map((item) => item.batchId)
                            .filter((id): id is string => Boolean(id))
                        );
                        if (restoredBatchId) batchIds.add(restoredBatchId);
                        batchIds.forEach((batchId) => forgetBatch(batchId));
                        setFiles([]);
                        setParsedResult(null);
                        setCompletedResults([]);
                        setSelectedResultFilename(null);
                        setCompanyDetailsForm({});
                        setShareholdingRows([]);
                        setLastParsedFile(null);
                        setReparseSheet(null);
                        setActiveDetailsTab("company");
                        setOnboardedCompanyId(companyId ?? null);
                      }}
                      title={
                        hasProcessingBatch
                          ? "You can clear files after processing finishes"
                          : undefined
                      }
                      className="text-sm font-medium text-stone-500 hover:text-stone-700 disabled:opacity-50 px-4 py-2"
                    >
                      Clear all
                    </button>
                  )}
                  <PrimaryCTAButton
                    event-name="Upload Onboarding Excels"
                    className="w-48 flex items-center justify-center"
                    loading={isUploading}
                    disabled={pendingCount === 0}
                    onClick={uploadAll}
                    buttonSize={ButtonSize.MEDIUM}
                  >
                    {pendingCount > 0
                      ? `Upload ${pendingCount} file${
                          pendingCount === 1 ? "" : "s"
                        }`
                      : "Upload"}
                  </PrimaryCTAButton>
                </HStack>

                {parsedResult && (
                  <VStack className="gap-6 pt-4 border-t border-stone-100">
                    {completedResults.length > 1 && (
                      <HStack className="w-full gap-1 border-b border-stone-200 px-5">
                        {completedResults.map((result) => (
                          <button
                            key={result.filename}
                            type="button"
                            title={result.filename}
                            className={`min-w-0 flex-1 truncate border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                              selectedResultFilename === result.filename
                                ? "border-primary text-primary"
                                : "border-transparent text-stone-700 hover:border-primary hover:text-primary"
                            }`}
                            onClick={() =>
                              selectCompletedResult(
                                result.data,
                                result.filename
                              )
                            }
                          >
                            <span className="block truncate">
                              {result.filename}
                            </span>
                          </button>
                        ))}
                      </HStack>
                    )}
                    {(parsedResult.warnings?.length ||
                      parsedResult.selectionMetadata) && (
                      <VStack className="gap-2 rounded-md border border-amber-300 bg-amber-50 p-4">
                        <p className="text-sm font-semibold text-amber-800">
                          AI extraction review
                        </p>
                        {parsedResult.warnings?.map((warning, index) => (
                          <p
                            key={`${warning}-${index}`}
                            className="text-xs text-amber-800"
                          >
                            • {warning}
                          </p>
                        ))}
                        {parsedResult.selectionMetadata && (
                          <p className="text-xs text-amber-800">
                            {Object.entries(parsedResult.selectionMetadata)
                              .map(
                                ([key, value]) =>
                                  `${key}: ${
                                    Array.isArray(value)
                                      ? value.join(", ")
                                      : value ?? "—"
                                  }`
                              )
                              .join(" · ")}
                          </p>
                        )}
                      </VStack>
                    )}
                    {lastParsedFile?.sheetNames &&
                      lastParsedFile.sheetNames.length > 0 && (
                        <HStack className="items-center gap-3 flex-wrap">
                          <p className="text-xs text-stone-500 shrink-0">
                            Parse again with a specific sheet (applies to both
                            company details and shareholding):
                          </p>
                          <Box className="w-52">
                            <Select
                              options={lastParsedFile.sheetNames.map(
                                (name) => ({ label: name, value: name })
                              )}
                              value={
                                reparseSheet
                                  ? { label: reparseSheet, value: reparseSheet }
                                  : null
                              }
                              onChange={(option) =>
                                setReparseSheet(option?.value || null)
                              }
                              formatOptionLabel={formatSheetOptionLabel}
                              isClearable
                              placeholder="Select sheet"
                              classNamePrefix="select2-selection"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 1400,
                                }),
                              }}
                            />
                          </Box>
                          <button
                            type="button"
                            disabled={isReparsing}
                            onClick={reparseWithSheet}
                            className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                          >
                            {isReparsing ? "Parsing…" : "Parse Again"}
                          </button>
                        </HStack>
                      )}

                    <Box className="bg-white rounded-md border border-stone-200">
                      <HStack className="border-b border-stone-100 px-3 gap-1">
                        <button
                          type="button"
                          onClick={() => setActiveDetailsTab("company")}
                          className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                            activeDetailsTab === "company"
                              ? "border-primary text-primary"
                              : "border-transparent text-stone-500 hover:text-stone-700"
                          }`}
                        >
                          Company Details
                        </button>
                        {shareholdingRows.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setActiveDetailsTab("shareholding")}
                            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                              activeDetailsTab === "shareholding"
                                ? "border-primary text-primary"
                                : "border-transparent text-stone-500 hover:text-stone-700"
                            }`}
                          >
                            Shareholding Details
                            <span
                              className={`rounded-full px-1.5 py-0.5 text-xxs font-semibold ${
                                activeDetailsTab === "shareholding"
                                  ? "bg-primary/10 text-primary"
                                  : "bg-stone-100 text-stone-500"
                              }`}
                            >
                              {shareholdingRows.length}
                            </span>
                          </button>
                        )}
                      </HStack>

                      <VStack className="gap-6 p-5">
                        {activeDetailsTab === "company" && (
                          <>
                            {missingRequiredFields.length > 0 && (
                              <HStack className="items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                                <Icon
                                  icon="mdi:alert-circle-outline"
                                  className="text-amber-500 shrink-0 mt-0.5"
                                  height={18}
                                />
                                <p className="text-xs text-amber-700">
                                  Missing required fields:{" "}
                                  {missingRequiredFields
                                    .map((field) => field.label)
                                    .join(", ")}
                                </p>
                              </HStack>
                            )}
                            {missingParsedFields.length > 0 && (
                              <p className="text-xs text-red-600">
                                AI marked these fields as missing:{" "}
                                {missingParsedFields.join(", ")}
                              </p>
                            )}

                            <HStack className="items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
                              <Icon
                                icon="mdi:information-outline"
                                className="text-blue-500 shrink-0 mt-0.5"
                                height={18}
                              />
                              <p className="text-xs text-blue-700">
                                These details will be overridden with the
                                details from the Excel file, if present.
                              </p>
                            </HStack>

                            <HStack className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                              {COMPANY_DETAIL_FIELDS.map((field) => (
                                <VStack key={field.key}>
                                  <Label className="text-sm font-medium whitespace-nowrap">
                                    {field.label}
                                    {field.required && <Required />}
                                  </Label>
                                  {field.key === "companyCurrency" ||
                                  field.key === "jurisdiction" ? (
                                    <Select
                                      options={(field.key === "companyCurrency"
                                        ? currencies
                                        : countries
                                      ).map((option) => ({
                                        label: option,
                                        value: option,
                                      }))}
                                      value={
                                        companyDetailsForm[field.key]
                                          ? {
                                              label: companyDetailsForm[
                                                field.key
                                              ] as string,
                                              value: companyDetailsForm[
                                                field.key
                                              ] as string,
                                            }
                                          : null
                                      }
                                      placeholder={`Select ${field.label}`}
                                      onChange={(option) =>
                                        setCompanyDetailsForm((prev) => {
                                          const next = {
                                            ...prev,
                                            [field.key]: option?.value,
                                          };
                                          if (
                                            field.key === "jurisdiction" &&
                                            option?.value
                                          ) {
                                            const matchedCurrency =
                                              getCurrencyForCountry(
                                                option.value
                                              );
                                            if (matchedCurrency) {
                                              next.companyCurrency =
                                                matchedCurrency;
                                            }
                                          }
                                          return next;
                                        })
                                      }
                                      isClearable
                                      classNamePrefix="select2-selection"
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                      styles={{
                                        ...inputMatchingSelectStyles,
                                        menuPortal: (base) => ({
                                          ...base,
                                          zIndex: 1400,
                                        }),
                                        control: (base, state) => ({
                                          ...inputMatchingSelectStyles.control(
                                            base,
                                            state
                                          ),
                                          borderColor:
                                            field.required &&
                                            !companyDetailsForm[field.key]
                                              ? "#f87171"
                                              : inputMatchingSelectStyles.control(
                                                  base,
                                                  state
                                                ).borderColor,
                                        }),
                                      }}
                                    />
                                  ) : COMMA_FORMATTED_FIELD_KEYS.includes(
                                      field.key
                                    ) ? (
                                    <Input
                                      type="text"
                                      inputMode="decimal"
                                      className={
                                        field.required &&
                                        !companyDetailsForm[field.key]
                                          ? "border-red-400"
                                          : ""
                                      }
                                      value={formatWithCommas(
                                        String(
                                          companyDetailsForm[field.key] ?? ""
                                        )
                                      )}
                                      placeholder={`Enter ${field.label}`}
                                      onChange={(e) =>
                                        setCompanyDetailsForm((prev) => ({
                                          ...prev,
                                          [field.key]: e.target.value.replace(
                                            /[^0-9.]/g,
                                            ""
                                          ),
                                        }))
                                      }
                                    />
                                  ) : (
                                    <Input
                                      type={field.type || "text"}
                                      className={
                                        field.required &&
                                        !companyDetailsForm[field.key]
                                          ? "border-red-400"
                                          : ""
                                      }
                                      value={
                                        (companyDetailsForm[field.key] as
                                          | string
                                          | number
                                          | undefined) ?? ""
                                      }
                                      placeholder={`Enter ${field.label}`}
                                      onChange={(e) =>
                                        setCompanyDetailsForm((prev) => ({
                                          ...prev,
                                          [field.key]: e.target.value,
                                        }))
                                      }
                                    />
                                  )}
                                  {field.required &&
                                    !companyDetailsForm[field.key] && (
                                      <p className="text-xxs text-red-500 mt-1">
                                        {field.label} is required
                                      </p>
                                    )}
                                </VStack>
                              ))}
                            </HStack>
                          </>
                        )}

                        {activeDetailsTab === "shareholding" &&
                          shareholdingRows.length > 0 && (
                            <ShareholdingDetailsGrid
                              rows={shareholdingRows}
                              currency={
                                companyDetailsForm.companyCurrency ?? undefined
                              }
                              onRowsChange={setShareholdingRows}
                            />
                          )}
                      </VStack>
                    </Box>
                  </VStack>
                )}
              </>
            )}
          </VStack>

          {parsedResult && (
            <HStack className="justify-between items-center gap-3 px-6 py-3 border-t border-stone-100 bg-white shrink-0">
              {allAccountUserEmails.length > 0 ? (
                <HStack className="items-center gap-1.5">
                  <CheckBoxWithLabel
                    id="give-access-all-users"
                    label="Give access to all users in the account"
                    labelclassname="text-sm text-stone-600 ml-2"
                    checked={giveAccessToAllUsers}
                    onChange={(e) => setGiveAccessToAllUsers(e.target.checked)}
                  />
                </HStack>
              ) : (
                <div />
              )}
              <HStack className="items-center gap-3">
                {isOnboardDisabled && (
                  <p className="text-xs text-red-500">
                    Fill all required fields to onboard
                  </p>
                )}
                <PrimaryCTAButton
                  event-name="Onboard Parsed Company"
                  className="w-48 flex items-center justify-center"
                  disabled={isOnboardDisabled}
                  loading={isOnboarding}
                  onClick={handleOnboard}
                  buttonSize={ButtonSize.MEDIUM}
                >
                  Onboard Company
                </PrimaryCTAButton>
              </HStack>
            </HStack>
          )}
        </VStack>
      )}

      <Dialog open={dialog.open} maxWidth="lg">
        <ValidationErrorsDialog
          errors={dialog.data || []}
          onPrimaryAction={() => setDialog({ open: false })}
          onSecondaryAction={() => setDialog({ open: false })}
        />
      </Dialog>
    </Box>
  );
}
